/**
 * The frame every clinic page sits in. Signed in, it is the shared app shell with the clinic's
 * navigation, the active clinic named above it, and a menu to sign out. Signed out, or on a screen
 * that is not the clinic's own (the customer's link request), it is the brand and a column, because
 * a navigation to screens that need a session would only lead to more sign-in prompts.
 *
 * A page that passes `title` gets the page header, which also sets the tab title. A page that
 * draws its own heading leaves it out.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { AppShell } from "@lattice/ui/AppShell";
import type { ContentWidth } from "@lattice/ui/Content";
import { Content } from "@lattice/ui/Content";
import { BuildingIcon, PeopleIcon } from "@lattice/ui/icons";
import { focusRing } from "@lattice/ui/focus";
import { Mark } from "@lattice/ui/Mark";
import type { Crumb, PageAction } from "@lattice/ui/PageHeader";
import { PageHeader } from "@lattice/ui/PageHeader";
import { signOut } from "../api.ts";
import { confirmLeave, navigate, usePath } from "../router.ts";
const styles = {
  brand: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1173whf",
    kAzted: "x1gg8mnh",
    kMwMTN: "x10dbofl",
    kybGjl: "x1hl2dhg",
    k63SB2: "xqu410z",
    kaIpWk: "x8wwnw1",
    $$css: true
  }
};

/** The screens every member of a clinic can open. Owners, practitioners and assistants share them. */
const NAV = [{
  label: "Clinic home",
  href: "/clinic",
  icon: <BuildingIcon />
}, {
  label: "Patients",
  href: "/clinic/patients",
  icon: <PeopleIcon />
}] as const;

/** Clinic home is current only on the home itself, Patients on the list and on every patient page. */
function isHere(href: string, path: string): boolean {
  return href === "/clinic" ? path === "/clinic" : path === href || path.startsWith(`${href}/`);
}
export interface ClinicShellProps {
  readonly title?: string;
  readonly description?: string;
  readonly crumbs?: readonly Crumb[];
  readonly actions?: readonly PageAction[];
  /** True once the person is known to be signed in. */
  readonly signedIn?: boolean;
  /** The active clinic, shown above the navigation so a member of several knows where they are. */
  readonly clinicName?: string;
  /** False on a customer's screen, which is not part of any clinic. */
  readonly clinicArea?: boolean;
  /** How wide the signed-in page may grow. A table of five columns takes the wide one. */
  readonly width?: ContentWidth;
  /** Signed-out frame only: centre a single message and its actions in the column. */
  readonly centred?: boolean;
  readonly children: ReactNode;
}
export function ClinicShell({
  title,
  description,
  crumbs,
  actions,
  signedIn = false,
  clinicName,
  clinicArea = true,
  width = "medium",
  centred = false,
  children
}: ClinicShellProps) {
  const path = usePath();
  const header = title === undefined ? null : <PageHeader title={title} siteName={clinicArea ? "Lattice Clinic" : "Lattice"} navigate={navigate} description={description} crumbs={crumbs} actions={actions} />;
  if (signedIn) return <AppShell brand="Lattice Clinic" navLabel="Clinic pages" navigate={navigate} nav={[{
    label: clinicName,
    items: NAV.map(item => ({
      ...item,
      current: isHere(item.href, path)
    }))
  }]} account={{
    name: "Clinic account",
    items: [{
      label: "Sign out",
      onSelect: () => {
        confirmLeave(() => {
          void signOut().then(() => navigate("/practitioners", {
            force: true
          }));
        });
      }
    }]
  }}>
        <Content width={width}>
          <div className="x78zum5 xdt5ytf xd3upel">
            {header}
            {children}
          </div>
        </Content>
      </AppShell>;
  return <div className="x1ov3xa9 x1l0w1cl xzb3lse x1cikjda x1rf3zrh xbz0q3o x179o8vp x1u8hu9d">
      <div className="xf2d64e xvueqy4">
        <header {...{
        0: {
          className: "x78zum5 x6s0dn4 x1qughib x1r0x45p x1wiri7k"
        },
        1: {
          className: "x78zum5 x6s0dn4 x1r0x45p x1wiri7k xl56j7k"
        }
      }[!!centred << 0]}>
          <a href="/" {...stylex.props(styles.brand, focusRing)}>
            <Mark size={22} />
            {/* One run of text with a real space, so the visible words read "Lattice Clinic" to a voice user. */}
            <span className="xzb3lse">
              Lattice
              {clinicArea ? <>
                  {" "}
                  <span className="x1jty25b xox936i">Clinic</span>
                </> : null}
            </span>
          </a>
        </header>
        <main {...{
        0: {
          className: "x78zum5 xdt5ytf xd3upel xjh1bw0"
        },
        1: {
          className: "x78zum5 xdt5ytf xd3upel x6s0dn4 x2b8uid x1j9m8v2"
        }
      }[!!centred << 0]}>
          {header}
          {children}
        </main>
      </div>
    </div>;
}
export const clinicText = {
  block: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x626il7",
    $$css: true
  },
  heading: {
    kogj98: "x1ghz6dp",
    kGuDYH: "x1u29x3t",
    k63SB2: "xqu410z",
    kQqvRs: "x1ntj4s9",
    kb6lSQ: "x1d3j7mx",
    kLWn49: "x1e5flnj",
    kN2L0X: "x1w2vvpw",
    $$css: true
  },
  lead: {
    kogj98: "x1ghz6dp",
    kMwMTN: "x1jty25b",
    kN2L0X: "x1fzhlzt",
    $$css: true
  },
  error: {
    kogj98: "x1ghz6dp",
    kGuDYH: "xl4dl4e",
    kMwMTN: "x156x1qg",
    $$css: true
  }
};